import { isString, throttle } from "lodash-es";
import type {
  LocationQueryRaw,
  RouteLocationNamedRaw,
  RouteLocationNormalizedLoadedGeneric,
  RouteParamsRawGeneric,
  RouteRecordNameGeneric,
} from "vue-router";
import { isNavigationFailure } from "vue-router";

import type { BoardType } from "@/model/baseTypes";
import { useNavigationStore } from "@/store/navigation";
import { useSessionStore } from "@/store/session";
import { useTeamStore } from "@/store/team";
import { useZoomStore } from "@/store/zoom";

import { getRouter } from ".";
import type { BoardTarget, LogoutReason, Params, Query, QueryImpl } from "./types";
import { PARAM, toQueryImpl } from "./types";

export async function goToLogin() {
  return goTo({ name: "login" });
}

export function goToLogout(reason: LogoutReason) {
  return goTo({ name: "logout", query: { reason } });
}

export function goToBoard(board: BoardTarget, query?: QueryImpl) {
  return goTo(mergeParams(boardParams(board), query));
}

function goTo(spec: { name?: RouteRecordNameGeneric; params?: Params; query?: LocationQueryRaw }) {
  return getRouter()
    .syncPush(() => ({
      name: spec?.name,
      params: spec?.params as RouteParamsRawGeneric,
      query: { feature: currentRoute().query?.feature, ...spec?.query },
    }))
    .catch(handleRouterFail);
}

export async function replaceQuery(query: Query) {
  return getRouter()
    .syncReplace(() => {
      const current = currentRoute().query as QueryImpl;
      const newQuery: QueryImpl = {
        zoom: query.zoom || current.zoom,
        scrollX: query.scrollX || current.scrollX,
        scrollY: query.scrollY || current.scrollY,
        searchText: query.search && "text" in query.search ? query.search.text : current.searchText,
        stickyId: "stickyId" in query ? query.stickyId : current.stickyId,
        searchFlag:
          query.search?.flag !== undefined ? query.search.flag || undefined : current.searchFlag,
        searchAssignee:
          query.search?.assignee !== undefined
            ? query.search.assignee || undefined
            : current.searchAssignee,
        searchReporter:
          query.search?.reporter !== undefined
            ? query.search.reporter || undefined
            : current.searchReporter,
        searchTeam:
          query.search?.team !== undefined ? query.search.team || undefined : current.searchTeam,
        searchArt:
          query.search?.art !== undefined ? query.search.art || undefined : current.searchArt,
        searchIteration:
          query.search?.iteration !== undefined
            ? query.search.iteration || undefined
            : current.searchIteration,
        searchType:
          query.search?.type !== undefined ? query.search.type || undefined : current.searchType,
        searchDepLink:
          query.search?.depLink !== undefined
            ? query.search.depLink || undefined
            : current.searchDepLink,
        searchStatus:
          query.search?.status !== undefined
            ? query.search.status || undefined
            : current.searchStatus,
        searchLink:
          query.search?.link !== undefined ? query.search.link || undefined : current.searchLink,
        searchUpperLevelLinks:
          query.search?.upperLevelLinks !== undefined
            ? query.search.upperLevelLinks || undefined
            : current.searchUpperLevelLinks,
        feature: current.feature,
        category: "category" in query ? query.category : current.category,
      };

      return { query: { ...current, ...(newQuery as Record<string, string | undefined>) } };
    })
    .catch(handleRouterFail);
}

const throttledReplaceQuery = throttle(() => {
  void replaceQuery({
    zoom: "" + useZoomStore().factor,
    scrollX: "" + Math.round(window.scrollX),
    scrollY: "" + Math.round(window.scrollY),
  });
}, 100);

export function pushZoomState() {
  if (currentRoute()?.name === "app" && !useZoomStore().zooming) {
    throttledReplaceQuery();
  }
}

export function boardUrl(
  session: string | undefined,
  board: BoardType,
  spec?: string,
  query?: string,
) {
  const params = boardParams({
    type: board,
    team: board === "team" && spec ? { name: spec } : undefined,
    name: board === "flex" && spec ? spec : undefined,
  });
  if (!params.team) {
    params.team = PARAM.CURRENT_TEAM;
  }
  if (session) {
    params.session = session;
  }
  const queryImpl = query ? toQueryImpl(new URLSearchParams(query).entries()) : {};
  const router = getRouter();

  return router.resolve(mergeParams(params, queryImpl));
}

export function fullUrl(query: QueryImpl) {
  const base = window.location.origin + window.location.pathname;
  const search = Object.entries(query)
    .map(([key, value]) => key + "=" + value)
    .join("&");
  const currentRoutePath = normalized(currentRoute()).path;

  return base + "#" + currentRoutePath + "?" + search;
}

export function openBoardInNewTab(board: BoardTarget, query?: QueryImpl) {
  const resolved = getRouter().resolve(mergeParams(boardParams(board), query));
  const url = window.location.origin + window.location.pathname + "#" + resolved.fullPath;
  window.open(url, "_blank");
}

/**
 * Returns a URI-encoded version of the route, with the "latest"
 * session parameter replaced by the unique name of the current session.
 */
function normalized(route: RouteLocationNormalizedLoadedGeneric) {
  if (route.params.session === PARAM.LATEST) {
    // Do NOT spread route into resolve, because the 'path' property it contains
    // causes resolve to return a non-encoded string (generally, resolve returns
    // a URI encoded string...)
    return getRouter().resolve({
      name: route.name,
      params: {
        ...route.params,
        session: useSessionStore().uniqueSessionName(useSessionStore().current),
      },
      query: route.query,
    });
  }
  return route;
}

export function currentRoute() {
  return getRouter()?.currentRoute?.value;
}

function handleRouterFail(fail?: unknown) {
  if (!isNavigationFailure(fail)) {
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject(fail);
  }
}

function mergeParams(params: Params, query?: QueryImpl): RouteLocationNamedRaw {
  return {
    params: {
      ...currentRoute().params,
      ...{ name: undefined },
      ...params,
    } as unknown as Record<string, string>, // the typing of vue-router is wrong: params can be undefined to remove them from the route
    query: { ...currentRoute().query, zoom: "1", ...query },
  };
}

function boardParams(target: BoardTarget): Params {
  if (isString(target)) {
    return { board: target };
  }
  if (target.session) {
    return { board: target.type, team: PARAM.LAST_TEAM, session: target.session };
  }
  if (target.type === "flex") {
    const name = target.name;
    return { board: "flex", ...(name && { name }) };
  }
  const team = target.team?.name || teamInArt(target.artId);
  return { board: target.type, ...(team && { team }) };

  function teamInArt(artId?: string) {
    if (artId) {
      const teamId = useNavigationStore().lastSelectedTeamInArtId(artId);
      const teams = useTeamStore().teams;
      const team =
        teams.find((team) => team.id === teamId) || teams.find((team) => team.artId === artId);
      return team?.name;
    }
  }
}
